import Spinner from "components/ui/Spinner";
import Empty from "components/ui/Empty";
import Pagination from "components/ui/Pagination";
import ReactTable from "components/ui/Table/Table";
import { type ReactNode } from "react";

/**
 * Paginated response shape
 */
type PaginatedData<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
};

/**
 * Props definition
 */
interface TableWithStateProps<T extends object> {
  data?: PaginatedData<T> | T[] | null;
  columns: any[];
  isLoading?: boolean;
  noSearchMessage?: string;
  noDataMessage: string;
  showPagination?: boolean;
  fontSize?: "xs" | "sm" | "md" | "lg" | string;
  getRowColorKey?: (row: T) => string | number;
  hideHeader?: boolean;
  topRow?: ReactNode;
}

/**
 * Type guard to detect paginated data
 */
function isPaginated<T>(
  data: PaginatedData<T> | T[] | null | undefined,
): data is PaginatedData<T> {
  return !!data && !Array.isArray(data) && typeof data === "object" && "content" in data;
}

export default function TableWithState<T extends object>({
  data,
  columns,
  isLoading,
  noSearchMessage,
  noDataMessage,
  showPagination = true,
  fontSize,
  getRowColorKey,
  hideHeader = false,
  topRow,
}: TableWithStateProps<T>) {
  /* ---------------- loading ---------------- */
  if (isLoading) {
    return <Spinner />;
  }

  /* ---------------- normalize data ---------------- */
  const tableData: T[] = !data ? [] : isPaginated(data) ? data.content : data;
  const hasTopRow = !!topRow;
  const hasTableData = tableData.length > 0;

  /* ---------------- no search done ---------------- */
  if (!data && !hasTopRow) {
    return <Empty>{noSearchMessage}</Empty>;
  }

  /* ---------------- no data ---------------- */
  if (!hasTableData && !hasTopRow) {
    return <Empty>{noDataMessage}</Empty>;
  }

  /* ---------------- render table ---------------- */
  return (
    <>
      <ReactTable
        data={tableData}
        columns={columns}
        fontSize={fontSize}
        getRowColorKey={getRowColorKey}
        hideHeader={hideHeader}
        topRow={topRow}
      />

      {/* Pagination only if paginated response */}
      {isPaginated(data) && hasTableData && showPagination && (
        <Pagination
          count={data.totalElements}
          totalPages={data.totalPages}
          first={data.first}
          last={data.last}
        />
      )}
    </>
  );
}